import React, { useState } from 'react';
import { Video, HelpCircle, X, CheckCircle, Copy } from 'lucide-react';

interface TavusVideoHelpProps {
  isOpen: boolean;
  onClose: () => void;
}

export function TavusVideoHelp({ isOpen, onClose }: TavusVideoHelpProps) {
  const [activeTab, setActiveTab] = useState<'overview' | 'scripts' | 'tips'>('overview');
  const [copiedScript, setCopiedScript] = useState<string | null>(null);

  const copyToClipboard = (text: string, scriptName: string) => {
    navigator.clipboard.writeText(text);
    setCopiedScript(scriptName);
    setTimeout(() => setCopiedScript(null), 2000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-red-500 to-orange-500 text-white">
          <div className="flex items-center gap-3">
            <HelpCircle className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-semibold">Tavus Video Guide</h2>
              <p className="text-sm opacity-90">How to create effective AI videos</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/20 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex">
            <button
              onClick={() => setActiveTab('overview')}
              className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'overview'
                  ? 'border-red-500 text-red-500'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Video className="w-4 h-4" />
              Overview
            </button>
            <button
              onClick={() => setActiveTab('scripts')}
              className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'scripts'
                  ? 'border-red-500 text-red-500'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <DocumentText className="w-4 h-4" />
              Sample Scripts
            </button>
            <button
              onClick={() => setActiveTab('tips')}
              className={`flex items-center gap-2 px-6 py-3 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'tips'
                  ? 'border-red-500 text-red-500'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <LightBulb className="w-4 h-4" />
              Best Practices
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-red-900 mb-4">About Tavus Video Creator</h3>
                <p className="text-red-700 mb-4">
                  Tavus is an AI-powered video creation platform that allows you to create personalized videos at scale. With Agent Store's Tavus integration, you can create engaging videos to explain your agents, demonstrate features, and provide technical guidance.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-red-800">
                  <div className="bg-white p-4 rounded-lg border border-red-100">
                    <div className="font-medium mb-2">Personalized Videos</div>
                    <p className="text-sm">Create videos that speak directly to your users with personalized content.</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-red-100">
                    <div className="font-medium mb-2">Easy Sharing</div>
                    <p className="text-sm">Share videos via links, embed them in your documentation, or download for offline use.</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-red-100">
                    <div className="font-medium mb-2">Multiple Templates</div>
                    <p className="text-sm">Choose from various templates for different use cases and audiences.</p>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">How It Works</h3>
                <div className="space-y-4">
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center text-red-600 font-bold flex-shrink-0">
                      1
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 mb-1">Select a Template</h4>
                      <p className="text-gray-600 text-sm">Choose from our pre-designed video templates based on your needs.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center text-red-600 font-bold flex-shrink-0">
                      2
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 mb-1">Write Your Script</h4>
                      <p className="text-gray-600 text-sm">Create a script for your video or use one of our pre-written templates.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center text-red-600 font-bold flex-shrink-0">
                      3
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 mb-1">Generate Your Video</h4>
                      <p className="text-gray-600 text-sm">Our AI will create a personalized video based on your script and chosen template.</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4">
                    <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center text-red-600 font-bold flex-shrink-0">
                      4
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 mb-1">Share and Embed</h4>
                      <p className="text-gray-600 text-sm">Share your video with users or embed it in your documentation.</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-xl p-6">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Use Cases</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-2">Agent Onboarding</h4>
                    <p className="text-sm text-gray-600">Create videos that explain how to use specific agents, their capabilities, and setup process.</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-2">Feature Demonstrations</h4>
                    <p className="text-sm text-gray-600">Show users how to use platform features like A2A protocol, UI generation, and deployment.</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-2">Technical Guidance</h4>
                    <p className="text-sm text-gray-600">Provide step-by-step technical guidance for developers implementing complex features.</p>
                  </div>
                  <div className="bg-white p-4 rounded-lg border border-gray-200">
                    <h4 className="font-medium text-gray-900 mb-2">Marketing Content</h4>
                    <p className="text-sm text-gray-600">Create engaging videos to promote your agents or platform features to potential users.</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'scripts' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Sample Scripts</h3>
                <p className="text-gray-600 mb-6">
                  Use these sample scripts as starting points for your videos. Click the copy button to use them in the video creator.
                </p>

                <div className="space-y-6">
                  <div className="border border-gray-200 rounded-xl overflow-hidden">
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                      <h4 className="font-medium text-gray-900">Platform Introduction</h4>
                      <button
                        onClick={() => copyToClipboard(
                          "Hi there! I'm excited to show you Agent Store, your ultimate hub for discovering, deploying, and monetizing AI agents. Our platform brings together thousands of AI agents from GitHub, Hugging Face, and other sources, making it easy to find the perfect solution for your needs. Let me walk you through some key features...",
                          'platform'
                        )}
                        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                      >
                        {copiedScript === 'platform' ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <Copy className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                    <div className="p-4 text-sm text-gray-600">
                      <p>
                        Hi there! I'm excited to show you Agent Store, your ultimate hub for discovering, deploying, and monetizing AI agents. Our platform brings together thousands of AI agents from GitHub, Hugging Face, and other sources, making it easy to find the perfect solution for your needs. Let me walk you through some key features...
                      </p>
                    </div>
                  </div>

                  <div className="border border-gray-200 rounded-xl overflow-hidden">
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                      <h4 className="font-medium text-gray-900">Agent Explanation</h4>
                      <button
                        onClick={() => copyToClipboard(
                          "Today I want to introduce you to one of our most powerful AI agents. This agent can help you with [specific task] and is perfect for [use case]. It's built using [technology] and has features like [key features]. Let me show you how to set it up and get the most out of it...",
                          'agent'
                        )}
                        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                      >
                        {copiedScript === 'agent' ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <Copy className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                    <div className="p-4 text-sm text-gray-600">
                      <p>
                        Today I want to introduce you to one of our most powerful AI agents. This agent can help you with [specific task] and is perfect for [use case]. It's built using [technology] and has features like [key features]. Let me show you how to set it up and get the most out of it...
                      </p>
                    </div>
                  </div>

                  <div className="border border-gray-200 rounded-xl overflow-hidden">
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                      <h4 className="font-medium text-gray-900">A2A Protocol Guide</h4>
                      <button
                        onClick={() => copyToClipboard(
                          "The Agent-to-Agent protocol is a game-changer for building complex AI systems. It allows different AI agents to communicate seamlessly, creating powerful multi-agent workflows. In this video, I'll explain how A2A works and show you how to implement it in your own projects...",
                          'a2a'
                        )}
                        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                      >
                        {copiedScript === 'a2a' ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <Copy className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                    <div className="p-4 text-sm text-gray-600">
                      <p>
                        The Agent-to-Agent protocol is a game-changer for building complex AI systems. It allows different AI agents to communicate seamlessly, creating powerful multi-agent workflows. In this video, I'll explain how A2A works and show you how to implement it in your own projects...
                      </p>
                    </div>
                  </div>

                  <div className="border border-gray-200 rounded-xl overflow-hidden">
                    <div className="bg-gray-50 px-4 py-3 border-b border-gray-200 flex justify-between items-center">
                      <h4 className="font-medium text-gray-900">Deployment Tutorial</h4>
                      <button
                        onClick={() => copyToClipboard(
                          "Deploying AI agents can be challenging, but with Agent Store, it's simple. In this video, I'll walk you through our streamlined deployment process, from configuration to going live. You'll learn how to set up your environment, manage API keys, and monitor your deployed agents...",
                          'deployment'
                        )}
                        className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                      >
                        {copiedScript === 'deployment' ? (
                          <CheckCircle className="w-5 h-5 text-green-500" />
                        ) : (
                          <Copy className="w-5 h-5" />
                        )}
                      </button>
                    </div>
                    <div className="p-4 text-sm text-gray-600">
                      <p>
                        Deploying AI agents can be challenging, but with Agent Store, it's simple. In this video, I'll walk you through our streamlined deployment process, from configuration to going live. You'll learn how to set up your environment, manage API keys, and monitor your deployed agents...
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-blue-900 mb-3">Script Structure Tips</h3>
                <div className="space-y-3 text-blue-700">
                  <p>
                    <span className="font-medium">Introduction (15-20 seconds):</span> Start with a greeting and briefly explain what the video will cover.
                  </p>
                  <p>
                    <span className="font-medium">Main Content (60-90 seconds):</span> Explain the key points, features, or steps in a clear, concise manner.
                  </p>
                  <p>
                    <span className="font-medium">Conclusion (15-20 seconds):</span> Summarize the main points and include a call to action.
                  </p>
                  <p>
                    <span className="font-medium">Total Length:</span> Aim for 90-120 seconds (approximately 200-300 words) for optimal engagement.
                  </p>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'tips' && (
            <div className="space-y-6">
              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Best Practices for AI Videos</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white border border-gray-200 rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-red-100 rounded-lg">
                        <MicIcon className="w-5 h-5 text-red-600" />
                      </div>
                      <h4 className="font-medium text-gray-900">Script Writing</h4>
                    </div>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>Use conversational language that sounds natural when spoken</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>Keep sentences short and avoid complex structures</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>Include pauses with commas and periods for natural speech</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>Read your script aloud to check for flow and clarity</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-blue-100 rounded-lg">
                        <Users className="w-5 h-5 text-blue-600" />
                      </div>
                      <h4 className="font-medium text-gray-900">Audience Engagement</h4>
                    </div>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>Address viewers directly using "you" to create connection</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>Start with a hook that captures attention in the first 10 seconds</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>Include a clear call-to-action at the end of your video</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>Focus on benefits rather than just features</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-green-100 rounded-lg">
                        <Zap className="w-5 h-5 text-green-600" />
                      </div>
                      <h4 className="font-medium text-gray-900">Technical Content</h4>
                    </div>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>Explain technical concepts in simple, accessible language</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>Break down complex processes into clear, sequential steps</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>Use analogies to explain complex concepts</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>Mention specific use cases to make abstract concepts concrete</span>
                      </li>
                    </ul>
                  </div>

                  <div className="bg-white border border-gray-200 rounded-xl p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <Share2 className="w-5 h-5 text-purple-600" />
                      </div>
                      <h4 className="font-medium text-gray-900">Distribution</h4>
                    </div>
                    <ul className="space-y-2 text-sm text-gray-600">
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>Embed videos in documentation for enhanced learning</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>Share videos in onboarding emails to new users</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>Include videos in help center articles and FAQs</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                        <span>Use videos in social media to promote your agents</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
                <h3 className="text-lg font-semibold text-yellow-900 mb-3">Things to Avoid</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-2">
                    <XCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-yellow-800">Overly Technical Jargon</p>
                      <p className="text-sm text-yellow-700">Avoid industry-specific terms without explanation</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <XCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-yellow-800">Long, Complex Sentences</p>
                      <p className="text-sm text-yellow-700">Keep sentences concise for better delivery</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <XCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-yellow-800">Filler Words</p>
                      <p className="text-sm text-yellow-700">Avoid "um," "like," "you know" in your script</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-2">
                    <XCircle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-yellow-800">Rushing Through Content</p>
                      <p className="text-sm text-yellow-700">Give viewers time to absorb information</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Icon components
function DocumentText(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
      <polyline points="14 2 14 8 20 8" />
      <line x1="16" y1="13" x2="8" y2="13" />
      <line x1="16" y1="17" x2="8" y2="17" />
      <line x1="10" y1="9" x2="8" y2="9" />
    </svg>
  );
}

function LightBulb(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M15 14c.2-1 .7-1.7 1.5-2.5 1-.9 1.5-2.2 1.5-3.5A6 6 0 0 0 6 8c0 1 .2 2.2 1.5 3.5.7.7 1.3 1.5 1.5 2.5" />
      <path d="M9 18h6" />
      <path d="M10 22h4" />
    </svg>
  );
}

function MicIcon(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z" />
      <path d="M19 10v2a7 7 0 0 1-14 0v-2" />
      <line x1="12" x2="12" y1="19" y2="22" />
    </svg>
  );
}

function Users(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
      <circle cx="9" cy="7" r="4" />
      <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
      <path d="M16 3.13a4 4 0 0 1 0 7.75" />
    </svg>
  );
}

function Zap(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
    </svg>
  );
}

function Share2(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="18" cy="5" r="3" />
      <circle cx="6" cy="12" r="3" />
      <circle cx="18" cy="19" r="3" />
      <line x1="8.59" x2="15.42" y1="13.51" y2="17.49" />
      <line x1="15.41" x2="8.59" y1="6.51" y2="10.49" />
    </svg>
  );
}

function XCircle(props: React.SVGProps<SVGSVGElement>) {
  return (
    <svg
      {...props}
      xmlns="http://www.w3.org/2000/svg"
      width="24"
      height="24"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <circle cx="12" cy="12" r="10" />
      <line x1="15" x2="9" y1="9" y2="15" />
      <line x1="9" x2="15" y1="9" y2="15" />
    </svg>
  );
}